/**
 * Gradient Color Utilities
 *
 * Core color interpolation logic for gradients.
 *
 * Note: Actual color scales and display configuration are intentionally
 * NOT included here - each frontend defines its own color schemes.
 * Only the pure interpolation algorithm is shared.
 */

/**
 * RGB color components
 */
interface RgbColor {
  r: number
  g: number
  b: number
}

/**
 * Parse a hex color string to RGB components
 */
export function hexToRgb(hex: string): RgbColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) {
    return { r: 0, g: 0, b: 0 }
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

/**
 * Convert RGB components to a hex color string
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Interpolate between colors in a gradient based on a normalized value (0-1)
 *
 * This is a pure mathematical function for smooth color transitions.
 *
 * @param normalizedValue - Value between 0 and 1
 * @param colors - Array of hex color strings defining the gradient
 * @returns Interpolated hex color string
 */
export function interpolateGradientColor(
  normalizedValue: number,
  colors: string[]
): string {
  if (colors.length === 0) return '#808080' // Gray fallback
  if (colors.length === 1) return colors[0]

  // Clamp value to 0-1 range
  const t = Math.max(0, Math.min(1, normalizedValue))

  // Find the two colors to interpolate between
  const segmentCount = colors.length - 1
  const scaledT = t * segmentCount
  const lowerIndex = Math.min(Math.floor(scaledT), segmentCount - 1)
  const upperIndex = lowerIndex + 1
  const localT = scaledT - lowerIndex

  const lowerColor = hexToRgb(colors[lowerIndex])
  const upperColor = hexToRgb(colors[upperIndex])

  // Linear interpolation
  const r = lowerColor.r + (upperColor.r - lowerColor.r) * localT
  const g = lowerColor.g + (upperColor.g - lowerColor.g) * localT
  const b = lowerColor.b + (upperColor.b - lowerColor.b) * localT

  return rgbToHex(r, g, b)
}
